import React, { useEffect, useMemo, useState } from "react";
import {
    Drawer,
    Stack,
    Group,
    Button,
    Textarea,
    TextInput,
    Text,
    Divider,
    Select,
    Alert,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";

import {
    PublicTransportBookingReadDto,
    UpdateMyTransportBookingDto,
} from "../../types/transportPublicTypes";
import { updateMyTransportBooking } from "../../api/transportPublicApi";
import { useTransportLocations } from "./useTransportLocations";

const RED = "#ff5e5e";

type Props = {
    opened: boolean;
    onClose: () => void;
    booking: PublicTransportBookingReadDto | null;
    eventId: number;
    email: string;
    whatsAppNumber: string;
    onSaved: () => void;
};

function isArrival(kind: any) {
    return kind === 1 || String(kind).toLowerCase() === "arrival";
}

function isDeparture(kind: any) {
    return kind === 2 || String(kind).toLowerCase() === "departure";
}

export default function EditMyBookingDrawer(props: Props) {
    const { opened, onClose, booking, eventId, email, whatsAppNumber, onSaved } = props;

    const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
    const [scheduledTime, setScheduledTime] = useState("");
    const [flightNo, setFlightNo] = useState("");
    const [pickUpPlace, setPickUpPlace] = useState("");
    const [dropOffPlace, setDropOffPlace] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [numberOfPersons, setNumberOfPersons] = useState("");
    const [note, setNote] = useState("");

    const { loading: loadingLoc, error: locError, pickupSuggestions, dropoffSuggestions } =
        useTransportLocations(String(eventId), "dk");

    const pickupOptions = useMemo(
        () => pickupSuggestions.map((name) => ({ value: name, label: name })),
        [pickupSuggestions]
    );

    const dropoffOptions = useMemo(
        () => dropoffSuggestions.map((name) => ({ value: name, label: name })),
        [dropoffSuggestions]
    );

    useEffect(() => {
        if (!opened || !booking) return;

        const d = (booking.scheduledDate ?? "").slice(0, 10);
        setScheduledDate(d ? new Date(`${d}T12:00:00`) : null);

        const t = (booking.scheduledTime ?? "").trim();
        setScheduledTime(t ? t.slice(0, 5) : "");

        setFlightNo(booking.flightNo ?? "");
        setPickUpPlace(booking.pickUpPlace ?? "");
        setDropOffPlace(booking.dropOffPlace ?? "");
        setContactPerson(booking.contactPerson ?? "");
        setNumberOfPersons(booking.numberOfPersons != null ? String(booking.numberOfPersons) : "");
        setNote(booking.note ?? "");
    }, [opened, booking]);

    const save = async () => {
        if (!booking) return;

        const dto: UpdateMyTransportBookingDto = {
            email,
            whatsAppNumber,
            scheduledDate: scheduledDate ? scheduledDate.toISOString().slice(0, 10) : null,
            scheduledTime: scheduledTime ? `${scheduledTime}:00` : null,
            flightNo: flightNo?.trim() ? flightNo.trim() : null,
            contactPerson: contactPerson?.trim() ? contactPerson.trim() : null,
            numberOfPersons: numberOfPersons ? Number(numberOfPersons) : null,
            note: note?.trim() ? note.trim() : null,

            // kun ét af felterne sendes afhængigt af kind
            pickUpPlace: isDeparture(booking.kind) ? (pickUpPlace?.trim() ? pickUpPlace.trim() : null) : null,
            dropOffPlace: isArrival(booking.kind) ? (dropOffPlace?.trim() ? dropOffPlace.trim() : null) : null,
        };

        try {
            await updateMyTransportBooking(eventId, booking.transportRequestId, dto);

            showNotification({
                color: "green",
                title: "Gemt",
                message: "Booking opdateret.",
            });

            onSaved();
            onClose();
        } catch (e: any) {
            showNotification({
                color: "red",
                title: "Fejl",
                message: e?.response?.data || "Kunne ikke gemme booking.",
            });
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size="md"
            title="Rediger booking"
            overlayProps={{ opacity: 0.25 }}
        >
            <Stack gap="md">
                <Text c="dimmed" size="sm">Ret dine bookingoplysninger.</Text>

                <Divider />

                <DateInput
                    label="Scheduled date"
                    placeholder="Vælg dato..."
                    value={scheduledDate}
                    onChange={setScheduledDate}
                />

                <TimeInput
                    label="Scheduled time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.currentTarget.value)}
                    withSeconds={false}
                />

                <TextInput
                    label="Flight number"
                    value={flightNo}
                    onChange={(e) => setFlightNo(e.currentTarget.value)}
                />

                {locError && (
                    <Alert color="yellow" title="Lokationer">
                        {locError}
                    </Alert>
                )}

                {isDeparture(booking?.kind) && (
                    <Select
                        label="Pick-up place"
                        placeholder={loadingLoc ? "Henter lokationer..." : "Vælg pick-up sted"}
                        data={pickupOptions}
                        value={pickUpPlace || null}
                        onChange={(value) => setPickUpPlace(value ?? "")}
                        searchable
                        nothingFoundMessage="Ingen steder fundet"
                    />
                )}

                {isArrival(booking?.kind) && (
                    <Select
                        label="Drop-off place"
                        placeholder={loadingLoc ? "Henter lokationer..." : "Vælg drop-off sted"}
                        data={dropoffOptions}
                        value={dropOffPlace || null}
                        onChange={(value) => setDropOffPlace(value ?? "")}
                        searchable
                        nothingFoundMessage="Ingen steder fundet"
                    />
                )}

                <TextInput
                    label="Contact person"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.currentTarget.value)}
                />

                <TextInput
                    label="Number of persons"
                    value={numberOfPersons}
                    onChange={(e) => setNumberOfPersons(e.currentTarget.value.replace(/\D/g, ""))}
                />

                <Textarea
                    label="Note"
                    placeholder="Evt. note..."
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    autosize
                    minRows={3}
                />

                <Group justify="space-between" mt="sm">
                    <Button variant="default" leftSection={<IconX size={16} />} onClick={onClose}>
                        Luk
                    </Button>

                    <Button
                        leftSection={<IconDeviceFloppy size={16} />}
                        onClick={save}
                        style={{ backgroundColor: RED }}
                    >
                        Gem
                    </Button>
                </Group>
            </Stack>
        </Drawer>
    );
}